import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Calendar, Clock, MapPin, ArrowLeft, User, FileText, Award, CheckCircle, XCircle, Loader2, UserCheck, UserPlus, Share2, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de botones para compartir
function ShareButtons({ title, url }) {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = url || window.location.href;
  const shareText = encodeURIComponent(title || document.title);
  const shareUrlEncoded = encodeURIComponent(shareUrl);
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500 flex items-center gap-1">
        <Share2 className="w-4 h-4" /> Compartir:
      </span>
      
      {/* WhatsApp */}
      <a
        href={`https://wa.me/?text=${shareText}%20${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
        title="Compartir en WhatsApp"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
      
      {/* Facebook */}
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        title="Compartir en Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </a>
      
      {/* Twitter/X */}
      <a
        href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-black hover:bg-gray-800 text-white transition-colors"
        title="Compartir en X (Twitter)"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </a>
      
      {/* LinkedIn */}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white transition-colors"
        title="Compartir en LinkedIn"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
      
      {/* Copiar enlace */}
      <button
        onClick={handleCopyLink}
        className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        title={copied ? '¡Copiado!' : 'Copiar enlace'}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function CursoDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, trabajador } = useAuth();
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para modal de inscripción (¿Sos matriculado?)
  const [showMatriculadoModal, setShowMatriculadoModal] = useState(false);
  
  // Estado para modal de cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelDni, setCancelDni] = useState('');
  const [cancelEmail, setCancelEmail] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);

  useEffect(() => {
    fetchCurso();
  }, [id]);

  const fetchCurso = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/cursos/${id}`);
      // Redirect 301-like al slug canónico si vinieron por un link viejo
      if (res.data?.slug_canonico && res.data.slug_canonico !== id) {
        navigate(`/capacitaciones/${res.data.slug_canonico}`, { replace: true });
        return;
      }
      setCurso(res.data);
    } catch (err) {
      setError('Curso no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Parsear la fecha como local para evitar problemas de timezone
    const parts = dateStr.split('T')[0].split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCancelInscripcion = async (e) => {
    e.preventDefault();
    setCancelLoading(true);
    setCancelError('');
    
    try {
      await axios.post(`${API_URL}/capacitaciones/cursos/${id}/cancelar-inscripcion`, {
        dni: cancelDni,
        email: cancelEmail
      });
      setCancelSuccess(true);
    } catch (err) {
      setCancelError(err.response?.data?.detail || 'Error al cancelar inscripción');
    } finally {
      setCancelLoading(false);
    }
  };

  const resetCancelModal = () => {
    setShowCancelModal(false);
    setCancelDni('');
    setCancelEmail('');
    setCancelError('');
    setCancelSuccess(false);
  };

  // Manejar click en "Inscribirme"
  const handleInscribirseClick = () => {
    // Si ya está autenticado, ir directo a inscripción (con datos precompletados)
    if (isAuthenticated) {
      navigate(`/capacitaciones/${id}/inscribirse`);
    } else {
      // Mostrar modal preguntando si es matriculado
      setShowMatriculadoModal(true);
    }
  };

  // Manejar respuesta del modal de matriculado
  const handleSoyMatriculado = () => {
    setShowMatriculadoModal(false);
    // Guardar la URL de retorno para después del login
    sessionStorage.setItem('redirectAfterLogin', `/capacitaciones/${id}/inscribirse`);
    navigate('/login');
  };

  const handleNoSoyMatriculado = () => {
    setShowMatriculadoModal(false);
    // Ir directo al formulario de inscripción (para otros profesionales)
    navigate(`/capacitaciones/${id}/inscribirse`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error || !curso) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <GraduationCap className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">{error || 'Curso no encontrado'}</p>
        <button onClick={() => goBack()} className="text-emerald-600 hover:underline">
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Image */}
        <div className="bg-gray-100 rounded-xl overflow-hidden mb-8">
          {curso.imagen ? (
            <img
              src={curso.imagen}
              alt={curso.nombre}
              className="w-full h-auto max-h-[500px] object-contain mx-auto"
            />
          ) : (
            <div className="aspect-video w-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50">
              <GraduationCap className="w-24 h-24 text-emerald-200" />
            </div>
          )}
        </div>

        {/* CTA Inscripción - Solo mobile, entre imagen y contenido */}
        <div className="lg:hidden mb-6">
          {curso.inscripciones_abiertas !== false ? (
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              data-testid="btn-inscribirse-mobile"
              onClick={handleInscribirseClick}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Inscribirme
            </Button>
          ) : (
            <Button className="w-full h-12 text-base" disabled data-testid="btn-inscribirse-mobile-cerrado">
              <CheckCircle className="w-5 h-5 mr-2" />
              Inscripciones cerradas
            </Button>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">{curso.nombre}</h1>
            
            {/* Botones de compartir */}
            <div className="pb-4 border-b">
              <ShareButtons 
                title={`${curso.nombre} - Capacitación del Colegio de Trabajadores Sociales`} 
                url={window.location.href}
              />
            </div>
            
            {curso.descripcion_corta && (
              <p className="text-lg text-gray-600">{curso.descripcion_corta}</p>
            )}

            {curso.descripcion_larga && (
              <div className="prose prose-gray max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Descripción</h3>
                <p className="text-gray-600 whitespace-pre-line">{curso.descripcion_larga}</p>
              </div>
            )}

            {curso.programa && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Programa / Contenidos</h3>
                <p className="text-gray-600 whitespace-pre-line">{curso.programa}</p>
              </div>
            )}

            {/* Instructores/Disertantes - Array de objetos */}
            {curso.instructores && Array.isArray(curso.instructores) && curso.instructores.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {curso.instructores.length === 1 ? 'Disertante' : 'Disertantes'}
                </h3>
                <div className="space-y-3">
                  {curso.instructores.map((instructor, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <span className="text-gray-800 font-medium block">{instructor.nombre}</span>
                        {instructor.descripcion && (
                          <p className="text-sm text-gray-600 mt-1">{instructor.descripcion}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Docentes - String legacy */}
            {curso.docentes && typeof curso.docentes === 'string' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Docentes
                </h3>
                <p className="text-gray-600">{curso.docentes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Información del curso</h3>
              <div className="space-y-3 text-sm">
                {/* Fecha y hora - mostrar "A coordinar" si aplica */}
                {curso.horario_a_coordinar ? (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Fecha y horario</p>
                      <p className="font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-block">
                        📅 A coordinar con el participante
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {(curso.fechas?.length > 0 || curso.fecha) && (
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-500">{curso.fechas?.length > 1 ? 'Fechas' : 'Fecha'}</p>
                          {curso.fechas?.length > 0 ? (
                            <div className="font-medium text-gray-900 space-y-1">
                              {curso.fechas.map((f, i) => (
                                <p key={i}>{formatDate(f)}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="font-medium text-gray-900">{formatDate(curso.fecha)}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {curso.hora && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-500">Horario</p>
                          <p className="font-medium text-gray-900">{curso.hora} hs</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {curso.duracion_horas && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Duración</p>
                      <p className="font-medium text-gray-900">{curso.duracion_horas} horas</p>
                    </div>
                  </div>
                )}
                {curso.modalidad && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Modalidad</p>
                      <p className="font-medium text-gray-900">{curso.modalidad}</p>
                    </div>
                  </div>
                )}
                {curso.lugar && (curso.modalidad === 'Presencial' || curso.modalidad === 'Híbrida') && (
                  <div className="flex items-start gap-3" data-testid="curso-detalle-lugar">
                    <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Lugar</p>
                      <p className="font-medium text-gray-900 whitespace-pre-line">{curso.lugar}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 space-y-3">
              {curso.inscripciones_abiertas !== false ? (
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700" 
                  data-testid="btn-inscribirse"
                  onClick={handleInscribirseClick}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Inscribirme
                </Button>
              ) : (
                <Button className="w-full" disabled data-testid="btn-inscribirse-cerrado">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Inscripciones cerradas
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={() => setShowCancelModal(true)}
                data-testid="btn-cancelar-inscripcion"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancelar inscripción
              </Button>
              
              <div className="border-t my-3"></div>
              
              <Link to={`/capacitaciones/${id}/emitir`}>
                <Button variant="outline" className="w-full" data-testid="btn-emitir">
                  <FileText className="w-4 h-4 mr-2" />
                  Emitir Constancia
                </Button>
              </Link>
              
              <Link to={`/capacitaciones/${id}/emitir?tipo=certificado`}>
                <Button variant="outline" className="w-full" data-testid="btn-certificado">
                  <Award className="w-4 h-4 mr-2" />
                  Emitir Certificado
                </Button>
              </Link>
            </div>

            {/* Note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <p className="font-medium mb-1">Nota importante</p>
              <p>La emisión de constancias y certificados requiere que tu asistencia haya sido registrada por el administrador.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Cancelación */}
      <Dialog open={showCancelModal} onOpenChange={resetCancelModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Cancelar Inscripción
            </DialogTitle>
            <DialogDescription>
              Ingresá los datos con los que te inscribiste para cancelar tu inscripción.
            </DialogDescription>
          </DialogHeader>
          
          {cancelSuccess ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Inscripción cancelada</h3>
              <p className="text-gray-600 text-sm mb-4">
                Te enviamos un email de confirmación. El cupo ha sido liberado.
              </p>
              <Button onClick={resetCancelModal}>Cerrar</Button>
            </div>
          ) : (
            <form onSubmit={handleCancelInscripcion} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cancel-dni">DNI</Label>
                <Input
                  id="cancel-dni"
                  placeholder="Ej: 12345678"
                  value={cancelDni}
                  onChange={(e) => setCancelDni(e.target.value)}
                  required
                  data-testid="cancel-dni-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cancel-email">Email</Label>
                <Input
                  id="cancel-email"
                  type="email"
                  placeholder="tu@email.com"
                  value={cancelEmail}
                  onChange={(e) => setCancelEmail(e.target.value)}
                  required
                  data-testid="cancel-email-input"
                />
              </div>
              
              {cancelError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {cancelError}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetCancelModal} className="flex-1">
                  Volver
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={cancelLoading}
                  data-testid="confirm-cancel-btn"
                >
                  {cancelLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cancelando...
                    </>
                  ) : (
                    'Confirmar cancelación'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Matriculado - ¿Sos matriculado de Lomas de Zamora? */}
      <Dialog open={showMatriculadoModal} onOpenChange={setShowMatriculadoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              ¿Sos matriculade de Lomas de Zamora?
            </DialogTitle>
            <DialogDescription className="pt-2">
              En casos de cupos limitados, priorizamos la inscripción de quienes son matriculades de nuestro distrito. 
              Si sos matriculade, iniciá sesión para completar tu inscripción.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={handleNoSoyMatriculado}
              className="w-full sm:w-auto"
            >
              No, no soy matriculade
            </Button>
            <Button 
              onClick={handleSoyMatriculado}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Sí, soy matriculade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Colegio Profesional de Trabajadorxs Sociales - Distrito Lomas de Zamora</p>
        </div>
      </footer>
    </div>
  );
}
